/**
 * src/types/charts/bar-race.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Bar race chart — animated horizontal bar race showing value changes
 * over discrete time frames.
 *
 * REQUIRES: FEATURES.EXTENDED_CHARTS + FEATURES.TIMELINE_MODE both true.
 *
 * DATA MODEL (flat array, one point per entity per frame):
 *   { name:'Apple', value:840, frame:0, frameLabel:'2020' }
 *   { name:'Apple', value:920, frame:1, frameLabel:'2021' }
 *   { name:'Google', value:700, frame:0, frameLabel:'2020' }
 *
 *   frame:number (0-indexed integer) — sort key for keyframe order.
 *   frameLabel:string                — human display ("2020", "Q1'22").
 *   name must be IDENTICAL across frames for the same entity.
 *   Color is assigned once per name from first occurrence — never changes.
 *
 * INPUT FORMAT (BarRaceDataGrid):
 *   Users enter data in wide format: rows=time periods, columns=entities.
 *   The grid converts to flat on every cell change.
 *
 * Chart2Video | CORSphere Software Inc.
 */
import { z } from 'zod';
import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 'smooth'    — linear posT. Constant-velocity movement. The professional default.
 *               Best for data with many rank changes — clean and readable.
 * 'snap'      — bars hold for 30% of each transition, then easeOutCubic to new ranks.
 *               More data-faithful — easier to read exact values at frame boundaries.
 * 'drift'     — easeOutQuart posT. Bars decelerate gracefully into position.
 *               Best for data journalism with slowly-changing rankings.
 *               Creates a "sliding into place" feel — like results being tabulated.
 * 'cinematic' — bars hold for 35% then rush to new positions simultaneously.
 *               Most dramatic. Best for year-end reveals or "who won" storytelling.
 *               All bars move at once after the hold, creating a "moment of truth" feel.
 */
export type BarRaceAnimationStyle = 'smooth' | 'snap' | 'drift' | 'cinematic';
export const BAR_RACE_ANIMATION_STYLES: BarRaceAnimationStyle[] = ['smooth', 'snap', 'drift', 'cinematic'];
// Bar race animation is the race itself — no per-element override makes sense.
export const BAR_RACE_ELEMENT_PANEL_STYLES: BarRaceAnimationStyle[] = [];

// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One entity's value at one point in time.
 *
 * CRITICAL CONSTRAINT: `name` must be IDENTICAL across all frames for the
 * same entity. The renderer builds entityColorMap from first occurrence —
 * a single character difference ("Goggle" vs "Google") creates a new entity
 * with a new color and breaks enter/exit animation.
 */
export interface BarRaceDataPoint extends ChartDataPoint {
  chartType:   'bar-race';
  /** Must be >= 0. Negative values are invalid (bar widths cannot be negative). */
  value:       number;
  /**
   * 0-indexed keyframe number. Used to sort and group snapshots.
   * Do NOT use timestamps — the renderer needs clean integer frame indices
   * for frame-to-frame interpolation math.
   */
  frame:       number;
  /**
   * Human-readable display string shown in the large corner label.
   * Examples: "2020", "Q1'22", "Week 12", "Round 3".
   * Defaults to String(frame) if omitted.
   */
  frameLabel?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface BarRaceSpacingConfig {
  chartType: 'bar-race';

  // ── Bar geometry ───────────────────────────────────────────────────────────
  /**
   * Pixel height of each racing bar in canvas pixels.
   * Overflow guard auto-shrinks proportionally if topN bars exceed 95% of chartH.
   * Default: 120.
   */
  barHeight:    number;
  /** Pixel gap between adjacent bars. Scales with barHeight via overflow guard. Default: 21. */
  barSpacing:   number;
  /** Corner radius on right edge (flat/thin styles only). Range: 0–30. Default: 8. */
  cornerRadius: number;

  // ── Race controls ──────────────────────────────────────────────────────────
  /** How many entities to show per frame. Range: 3–15. Default: 10. */
  topN: number;

  // ── Bar visual style ───────────────────────────────────────────────────────
  /**
   * Visual shape of each racing bar.
   *   'flat'    — flat left edge, cornerRadius on right. The classic bar race shape.
   *   'pill'    — fully rounded both ends (capsule). Modern social-content aesthetic.
   *   'block'   — sharp rectangle. BBC / FT data-journalism editorial look.
   *   'thin'    — flat but only 40% of the track height. Elegant strip with large cap.
   * Default: 'flat'
   */
  barStyle?: 'flat' | 'pill' | 'block' | 'thin';

  /**
   * When false, the bar rectangle is not drawn — only the cap circle appears
   * at the proportional value position. Creates a "bubble race" aesthetic.
   * Entity names, ranks, and value labels still render normally.
   * Use with capSizeMultiplier 0.80–1.20 for visually prominent bubbles.
   * Default: true
   */
  showBarFill?: boolean;

  // ── Cap circle ─────────────────────────────────────────────────────────────
  /** Show colored circle at bar tip (flag/logo holder). Default: true. */
  showCap?: boolean;

  /**
   * Cap circle radius as fraction of bar height. Range: 0.15–1.20.
   * 0.42 = cap ~84% of bar height (balanced default).
   * 0.50 = cap diameter = bar height (Flourish-style prominent).
   * 0.80–1.20 = oversized cap for showBarFill=false "bubble race" mode.
   * Default: 0.42
   */
  capSizeMultiplier?: number;

  /**
   * Shape of the cap indicator.
   *   'circle'        — default round cap.
   *   'square'        — square cap (good for brand logos).
   *   'roundedSquare' — square with rounded corners.
   *   'diamond'       — rotated 45° square. Good for racing/award aesthetics.
   * Per-entity override available via styleOverride.capShape in ElementPanel.
   * Default: 'circle'
   */
  capShape?: 'circle' | 'square' | 'roundedSquare' | 'diamond';

  /**
   * Width of the ring border around the cap circle in canvas px.
   * 0 = no border (cleaner look for images with their own defined edge).
   * Default: 2
   */
  capBorderWidth?: number;

  // ── Label visibility ───────────────────────────────────────────────────────
  /** Show value counter right of bar. Default: true. */
  showValue:      boolean;
  /** Show rank number left of entity name. Default: true. */
  showRank:       boolean;
  /** Show entity name in left column (right-aligned to chartX). Default: true. */
  showEntityName: boolean;
  /** Show large corner frame label (cross-fades between frames). Default: true. */
  showFrameLabel: boolean;

  // ── Scale axis ─────────────────────────────────────────────────────────────
  /**
   * Show vertical scale axis (grid lines + numeric labels above bars).
   * Labels and grid positions lerp with maxValue between frames.
   * Default: true
   */
  showScaleAxis?: boolean;
  /** Opacity of scale axis grid lines. Range: 0–1. Default: 0.35. */
  scaleAxisGridOpacity?: number;
  /** Opacity of scale axis numeric labels. Range: 0–1. Default: 0.85. */
  scaleAxisLabelOpacity?: number;
  /** Font size for scale axis labels in canvas px. Undefined = auto. */
  scaleAxisFontSize?: number;
  /** Color for scale axis labels + grid lines. Undefined = auto (adapts to bg). */
  scaleAxisColor?: string;
  /** Title text shown above the scale axis (e.g. "Revenue ($B)", "Population"). Default: none. */
  scaleAxisLabel?: string;

  // ── Frame label styling ────────────────────────────────────────────────────
  /** Opacity of the large corner year/period label. Range: 0–1. Default: 0.80. */
  frameLabelOpacity:      number;
  /** Canvas px. Undefined = auto (17% of chartH). */
  frameLabelFontSize?:    number;
  frameLabelFontFamily?:  string;   // Default: 'Arial'
  frameLabelFontWeight?:  string;   // Default: '300' (light weight for cinematic feel)
  frameLabelColor?:       string;   // Undefined = auto (dark on light bg, light on dark).
  frameLabelOffsetX?:     number;   // Canvas px nudge from lower-right anchor. Default: 0.
  frameLabelOffsetY?:     number;   // Canvas px nudge from lower-right anchor. Default: 0.

  // ── Entity name styling (left column text) ─────────────────────────────────
  entityNameFontSize?:    number;   // Canvas px. Undefined = auto (32% of scaledBarH).
  entityNameFontWeight?:  string;   // Default: '600'
  entityNameFontFamily?:  string;   // Default: 'Arial'
  entityNameColor?:       string;   // Undefined = auto (textOnBg).

  // ── Value counter styling (right of cap) ──────────────────────────────────
  valueLabelFontSize?:    number;   // Canvas px. Undefined = auto (28% of scaledBarH).
  valueLabelFontWeight?:  string;   // Default: '700'
  valueLabelFontFamily?:  string;   // Default: 'Arial'
  valueLabelColor?:       string;   // Undefined = auto.

  // ── Rank number styling (left of entity name) ─────────────────────────────
  rankFontSize?:    number;         // Canvas px. Undefined = auto (24% of scaledBarH).
  rankFontWeight?:  string;         // Default: '400'
  rankFontFamily?:  string;         // Default: 'Arial'
  rankColor?:       string;         // Undefined = auto (mutedOnBg).
}

// ─────────────────────────────────────────────────────────────────────────────
// ZOD SCHEMA (for validateConfig.ts discriminated union)
// ─────────────────────────────────────────────────────────────────────────────

export const barRaceSpacingZodSchema = z.object({
  chartType:    z.literal('bar-race'),
  barHeight:    z.number().min(20).max(300),
  barSpacing:   z.number().min(2).max(80),
  cornerRadius: z.number().min(0).max(30),
  topN:         z.number().int().min(3).max(15),
  barStyle:          z.enum(['flat', 'pill', 'block', 'thin']).optional(),
  showBarFill:       z.boolean().optional(),
  showCap:           z.boolean().optional(),
  capSizeMultiplier: z.number().min(0.15).max(1.20).optional(),  // extended from 0.60
  capShape:          z.enum(['circle', 'square', 'roundedSquare', 'diamond']).optional(),
  capBorderWidth:    z.number().min(0).max(10).optional(),
  showValue:         z.boolean(),
  showRank:          z.boolean(),
  showEntityName:    z.boolean(),
  showFrameLabel:    z.boolean(),
  showScaleAxis:          z.boolean().optional(),
  scaleAxisGridOpacity:   z.number().min(0).max(1).optional(),
  scaleAxisLabelOpacity:  z.number().min(0).max(1).optional(),
  scaleAxisFontSize:      z.number().min(8).max(60).optional(),
  scaleAxisColor:         z.string().optional(),
  scaleAxisLabel:         z.string().optional(),
  frameLabelOpacity:     z.number().min(0).max(1),
  frameLabelFontSize:    z.number().min(12).max(400).optional(),
  frameLabelFontFamily:  z.string().optional(),
  frameLabelFontWeight:  z.string().optional(),
  frameLabelColor:       z.string().optional(),
  frameLabelOffsetX:     z.number().optional(),
  frameLabelOffsetY:     z.number().optional(),
  entityNameFontSize:    z.number().min(8).max(120).optional(),
  entityNameFontWeight:  z.string().optional(),
  entityNameFontFamily:  z.string().optional(),
  entityNameColor:       z.string().optional(),
  valueLabelFontSize:    z.number().min(8).max(120).optional(),
  valueLabelFontWeight:  z.string().optional(),
  valueLabelFontFamily:  z.string().optional(),
  valueLabelColor:       z.string().optional(),
  rankFontSize:          z.number().min(8).max(120).optional(),
  rankFontWeight:        z.string().optional(),
  rankFontFamily:        z.string().optional(),
  rankColor:             z.string().optional(),
});

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_RACE_DEFAULTS: ChartConfigBase<BarRaceSpacingConfig> = {
  chartType: 'bar-race',
  content: {
    titles:    [makeTitle('Bar Chart Race')],
    subtitles: [],
    infoBoxes: [],
  },
  animation: {
    duration:  15,
    style:     'smooth',
    textSpeed: 1,
    physics:   { bounce: 0.35, springFreq: 2.5, staggerSpread: 0.45 },
  },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    // Left 260px: entity names right-aligned in left column + rank numbers.
    // Right 180px: cap circle + value labels overflow right of bar end.
    chartMargin: { top: 180, right: 180, bottom: 80, left: 260 },
  },
  axes:       { ...DEFAULT_AXES },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:         'bar-race',
    barHeight:         120,
    barSpacing:        21,
    cornerRadius:      8,
    topN:              10,
    barStyle:          'flat',
    showBarFill:       true,
    showCap:           true,
    capSizeMultiplier: 0.42,
    capShape:          'circle',
    capBorderWidth:    2,
    showValue:         true,
    showRank:          true,
    showEntityName:    true,
    showFrameLabel:    true,
    showScaleAxis:          true,
    scaleAxisGridOpacity:   0.35,
    scaleAxisLabelOpacity:  0.85,
    scaleAxisLabel:         'Market Cap ($B)',
    frameLabelOpacity: 0.40,
    frameLabelColor:   '#000000',
    frameLabelFontWeight: '300',
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES (agent pipeline grounding)
// ALL existing notes preserved + new notes for Sprint D3 fields added.
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_RACE_SCHEMA_NOTES: Record<string, SchemaNote> = {
  // ── Existing notes (preserved from original) ───────────────────────────────
  'bar-race.barHeight': {
    description: 'Pixel height of each racing bar. Overflow guard auto-shrinks both barH and barGap proportionally if topN bars would exceed 95% of chartH.',
    type:    'number',
    range:   '20–300',
    default: 120,
    unit:    'canvas px',
    affects: ['bar height', 'number of visible bars', 'vertical density'],
    bounds:  ['Renderer auto-shrinks if overflow — no manual calculation needed.'],
    synergizes: ['bar-race.barSpacing', 'bar-race.topN'],
    tensions:   ['bar-race.topN — above 12, bars shrink automatically to fit.'],
    agentNote: 'Use 80–120 for topN 8–10. Use 50–80 for topN 12–15. On 9:16 portrait canvas, 120px fills ~83% of chartH with topN=10.',
  },
  'bar-race.barSpacing': {
    description: 'Pixel gap between adjacent bars. Scales proportionally with barHeight via overflow guard.',
    type:    'number',
    range:   '2–80',
    default: 21,
    unit:    'canvas px',
    affects: ['visual density between bars'],
    bounds:  [],
    synergizes: ['bar-race.barHeight'],
    tensions:   ['bar-race.topN — overflow guard shrinks barGap and barH by the same factor.'],
    agentNote: 'Default 21 is well-calibrated for barHeight 120. Reduce to 8–14 for topN > 12. Increase to 30–40 for dramatic spacing with topN ≤ 6.',
  },
  'bar-race.topN': {
    description: 'Maximum entities shown per frame. Entities below topN hide and re-enter smoothly when they climb back into view. frameStates tracks ALL entities so entries animate from their true off-screen rank position.',
    type:    'number',
    range:   '3–15',
    default: 10,
    unit:    'count (integer)',
    affects: ['number of visible bars', 'vertical space usage'],
    bounds:  ['If dataset has fewer unique entities than topN, all are shown.'],
    synergizes: ['bar-race.barHeight', 'bar-race.barSpacing'],
    tensions:   ['topN above 10 — renderer auto-fits bar height to prevent overflow.'],
    agentNote: 'topN=10 is the journalism standard (Burn-Murdoch/Flourish default). Use topN=5 for dramatic races with few competitors.',
  },
  'bar-race.showRank': {
    description: 'Show rank number (1, 2, 3...) left of each bar in the left margin.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['rank number label'],
    bounds:  [],
    synergizes: [],
    tensions:   [],
    agentNote: 'Keep true — rank numbers answer "who is winning?" without requiring viewers to count bars.',
  },
  'bar-race.showEntityName': {
    description: 'Show entity name in the left column, right-aligned to the chart area left edge.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['entity name label in left column'],
    bounds:  [],
    synergizes: ['bar-race.entityNameFontSize', 'bar-race.entityNameColor'],
    tensions:   [],
    agentNote: 'Always keep true. Names in the left column are the professional bar race pattern — they never overlap bars even during rank transitions.',
  },
  'bar-race.showValue': {
    description: 'Show value counter to the right of the cap circle.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['value counter label right of bar'],
    bounds:  [],
    synergizes: ['bar-race.valueLabelFontSize', 'bar-race.valueLabelColor'],
    tensions:   [],
    agentNote: 'Keep true — the value counter provides the quantitative context the bar length alone cannot.',
  },
  'bar-race.showFrameLabel': {
    description: 'Show the large time label in the lower-right corner. Cross-fades between consecutive labels during transitions.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['corner frame label'],
    bounds:  [],
    synergizes: ['bar-race.frameLabelOpacity', 'bar-race.frameLabelFontSize', 'bar-race.frameLabelOffsetX', 'bar-race.frameLabelOffsetY'],
    tensions:   [],
    agentNote: 'Always keep true. The frame label is the single most important contextual element in a bar race — it tells viewers what time period they are seeing.',
  },
  'bar-race.frameLabelOpacity': {
    description: 'Opacity of the large corner frame label. 0 = invisible, 1 = fully opaque.',
    type:    'number',
    range:   '0–1',
    default: 0.40,
    unit:    'opacity fraction',
    affects: ['frame label readability'],
    bounds:  ['Only applies when showFrameLabel is true.'],
    synergizes: ['bar-race.showFrameLabel', 'bar-race.frameLabelColor'],
    tensions:   [],
    agentNote: 'Default 0.40 is a subtle watermark (classic Flourish/Observable style). Set 0.70–0.90 for a prominent label that dominates the lower-right corner.',
  },
  'bar-race.frameLabelFontSize': {
    description: 'Font size of the corner frame label in canvas px. Undefined = auto (17% of chartH, scales with format).',
    type:    'number',
    range:   '12–400',
    default: 'auto (17% of chartH)',
    unit:    'canvas px',
    affects: ['corner frame label size'],
    bounds:  ['Canvas px are absolute in the 1920×1080 coordinate space.'],
    synergizes: ['bar-race.showFrameLabel'],
    tensions:   [],
    agentNote: 'Leave undefined for proportional sizing. Set explicitly only for a specific locked size regardless of canvas format.',
  },
  'bar-race.frameLabelOffsetX': {
    description: 'Horizontal nudge of the frame label from its default lower-right anchor, in canvas px.',
    type:    'number',
    range:   'any integer',
    default: 0,
    unit:    'canvas px',
    affects: ['frame label horizontal position'],
    bounds:  ['Positive = moves right. Negative = moves left into the chart area.'],
    synergizes: ['bar-race.frameLabelOffsetY'],
    tensions:   [],
    agentNote: 'Default 0 places the label flush to the right edge of chartW. Use negative values (e.g. -100) to overlap with the bars for an integrated look.',
  },
  'bar-race.frameLabelOffsetY': {
    description: 'Vertical nudge of the frame label from its default lower-right anchor, in canvas px.',
    type:    'number',
    range:   'any integer',
    default: 0,
    unit:    'canvas px',
    affects: ['frame label vertical position'],
    bounds:  ['Positive = moves down. Negative = moves up into the chart area.'],
    synergizes: ['bar-race.frameLabelOffsetX'],
    tensions:   [],
    agentNote: 'Default 0 places the label at chartY + chartH. Use negative values to move the label up within the bars area.',
  },
  'bar-race.entityNameFontSize': {
    description: 'Font size for entity names in the left column, in canvas px. Undefined = auto (32% of scaledBarH, clamped to column width).',
    type:    'number',
    range:   '8–120',
    default: 'auto (32% of scaledBarH)',
    unit:    'canvas px',
    affects: ['entity name label in left column'],
    bounds:  ['Auto-clamps: font never produces names wider than the column.', 'Text is auto-truncated with ellipsis if entity name still exceeds column.'],
    synergizes: ['bar-race.barHeight', 'layout.chartMargin.left'],
    tensions:   [],
    agentNote: 'Leave undefined — auto-sizing ensures names fit the column at all margin/format combinations. Only set manually if you need a specific locked size.',
  },
  'bar-race.entityNameColor': {
    description: 'Color of entity names in the left column. Undefined = auto (dark on light bg, light on dark bg).',
    type:    'string',
    range:   'any hex color',
    default: 'auto (textOnBg)',
    unit:    'hex color',
    affects: ['entity name label color'],
    bounds:  [],
    synergizes: [],
    tensions:   [],
    agentNote: 'Leave undefined — auto color ensures readability on both light and dark backgrounds.',
  },
  'bar-race.valueLabelFontSize': {
    description: 'Font size for the value counter right of each bar, in canvas px. Undefined = auto (28% of scaledBarH).',
    type:    'number',
    range:   '8–120',
    default: 'auto (28% of scaledBarH)',
    unit:    'canvas px',
    affects: ['value counter size'],
    bounds:  [],
    synergizes: ['bar-race.barHeight'],
    tensions:   [],
    agentNote: 'Leave undefined for proportional sizing. Per-entity font override available via ElementPanel → Value label section.',
  },
  'bar-race.valueLabelColor': {
    description: 'Color of the value counter right of each bar. Undefined = auto (near-black on light bg, near-white on dark bg).',
    type:    'string',
    range:   'any hex color',
    default: 'auto',
    unit:    'hex color',
    affects: ['value counter color'],
    bounds:  [],
    synergizes: [],
    tensions:   [],
    agentNote: 'Leave undefined. Auto color ensures readability. Per-entity override available via ElementPanel → Value label section.',
  },
  'bar-race.rankFontSize': {
    description: 'Font size for rank numbers in the far-left margin, in canvas px. Undefined = auto (24% of scaledBarH).',
    type:    'number',
    range:   '8–120',
    default: 'auto (24% of scaledBarH)',
    unit:    'canvas px',
    affects: ['rank number size'],
    bounds:  [],
    synergizes: ['bar-race.barHeight'],
    tensions:   [],
    agentNote: 'Leave undefined for proportional sizing.',
  },
  'bar-race.rankColor': {
    description: 'Color of rank numbers. Undefined = auto (muted secondary color — slate-400 on dark, gray-500 on light).',
    type:    'string',
    range:   'any hex color',
    default: 'auto (mutedOnBg)',
    unit:    'hex color',
    affects: ['rank number color'],
    bounds:  [],
    synergizes: [],
    tensions:   [],
    agentNote: 'Default auto is deliberately muted — rank numbers are secondary to bar length and entity name. Lighten to #e2e8f0 if more prominence is needed.',
  },

  // ── Data point notes ───────────────────────────────────────────────────────
  'dataPoint.bar-race.name': {
    description: 'Entity name — the race participant. Must be IDENTICAL across all frames for the same entity. Case-sensitive.',
    type:    'string',
    range:   'any non-empty string',
    default: '',
    unit:    'string',
    affects: ['bar label in left column', 'entity identity', 'color assignment', 'enter/exit animation'],
    bounds:  ['A single character difference creates a new entity. "Google" and "google" are separate.'],
    synergizes: ['dataPoint.bar-race.frame'],
    tensions:   [],
    agentNote: 'The entityColorMap is built from first occurrence of each unique name. Ensure exact spelling consistency across all frames. Mismatches create phantom entities with broken animations.',
  },
  'dataPoint.bar-race.frame': {
    description: '0-indexed frame number. Determines animation order. Frame 0 = first snapshot; frame N-1 = final state.',
    type:    'number',
    range:   '0 to N-1 (integer)',
    default: 0,
    unit:    'frame index',
    affects: ['animation order', 'frame grouping', 'enter/exit transitions'],
    bounds:  ['Must be a non-negative integer. Minimum 2 distinct frame values required.'],
    synergizes: ['dataPoint.bar-race.frameLabel'],
    tensions:   [],
    agentNote: 'Use sequential integers starting from 0. Never use timestamps — the renderer needs integer indices for interpolation math. Use frameLabel to display human-readable dates.',
  },
  'dataPoint.bar-race.frameLabel': {
    description: 'Human-readable label for this frame shown in the corner. Examples: "2020", "Q1 2023", "Week 12".',
    type:    'string',
    range:   'any string; defaults to String(frame) if omitted',
    default: 'String(frame)',
    unit:    'string',
    affects: ['corner frame label display'],
    bounds:  ['All data points in the same frame should have the same frameLabel.'],
    synergizes: ['dataPoint.bar-race.frame', 'bar-race.showFrameLabel'],
    tensions:   [],
    agentNote: 'Keep labels short (4–8 chars) — the corner label renders at ~17% of chartH. Set on every data point even though only one per frame is displayed.',
  },
  'dataPoint.bar-race.value': {
    description: 'Entity value at this frame. Must be >= 0. Bar width is proportional relative to max value among topN entities in the current frame.',
    type:    'number',
    range:   '>= 0',
    default: 0,
    unit:    'any positive number',
    affects: ['bar width', 'rank ordering', 'value label counter'],
    bounds:  ['Negative values fail validation.', 'Value 0 produces zero-width bar — entity appears as cap-only at chartX.'],
    synergizes: ['bar-race.topN'],
    tensions:   [],
    agentNote: 'Use cumulative values (total at each time point), not deltas. Bar races show absolute values per frame. The renderer normalizes relative to topN max, not the global dataset max.',
  },
  'bar-race.styleOverride.labelText': {
    description: 'Overrides the entity name displayed in the left column. Does NOT affect the value counter. Use to shorten long names (e.g. "Microsoft Corporation" → "MSFT").',
    type:    'string',
    range:   'any string',
    default: 'entity name from data',
    unit:    'string',
    affects: ['entity name in left column'],
    bounds:  ['Only visible when showEntityName is true. Text is auto-truncated with ellipsis if it exceeds column width.'],
    synergizes: ['bar-race.showEntityName'],
    tensions:   [],
    agentNote: 'labelText overrides the display name in the left column only. The value counter always shows the numerical value. Set via ElementPanel → Entity label → Display name.',
  },

  // ── New Sprint D3 notes ────────────────────────────────────────────────────
  'bar-race.barStyle': {
    description: 'Visual shape: flat (classic right-rounded), pill (capsule), block (sharp rect), thin (40% height strip).',
    type:    'string',
    range:   "'flat' | 'pill' | 'block' | 'thin'",
    default: 'flat',
    unit:    'enum',
    affects: ['bar visual shape'],
    bounds:  ['cornerRadius only applies to flat and thin styles.'],
    synergizes: ['bar-race.cornerRadius', 'bar-race.showCap'],
    tensions:   [],
    agentNote: "Use 'flat' for standard professional. 'pill' for modern social content. 'block' for news/editorial (BBC, NYT). 'thin' combined with large cap for a minimalist track-race look.",
  },
  'bar-race.showBarFill': {
    description: 'When false, bar rectangle is hidden — only the cap circle appears at the proportional value position. Entity names, ranks, and value counters still render.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['bar rect visibility'],
    bounds:  [],
    synergizes: ['bar-race.showCap', 'bar-race.capSizeMultiplier'],
    tensions:   [],
    agentNote: 'Set false + capSizeMultiplier 0.80–1.20 for a "bubble race" where entities are represented by large circles only. This is a distinctive aesthetic not available in Flourish or amCharts.',
  },
  'bar-race.showCap': {
    description: 'Show colored circle indicator at bar tip. Acts as flag/logo holder — set styleOverride.imageUrl via ElementPanel to show an image inside.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['bar tip indicator'],
    bounds:  [],
    synergizes: ['bar-race.capSizeMultiplier', 'bar-race.capShape'],
    tensions:   [],
    agentNote: 'Keep true when using imageUrl. The cap IS the image container. Set false only for a minimal flat-bar aesthetic with no indicator.',
  },
  'bar-race.capSizeMultiplier': {
    description: 'Cap circle radius as fraction of bar height. Supports up to 1.20 for showBarFill=false bubble mode.',
    type:    'number',
    range:   '0.15–1.20',
    default: 0.42,
    unit:    'fraction of scaledBarH',
    affects: ['cap circle size'],
    bounds:  ['0.50 = cap diameter equals bar height. Above 0.60, cap extends above/below bar bounds — use showBarFill=false.'],
    synergizes: ['bar-race.showBarFill', 'bar-race.capShape'],
    tensions:   [],
    agentNote: 'Keep at 0.42 for standard balanced proportions. Use 0.80–1.20 with showBarFill=false for pure bubble mode. Use 0.25 for a subtle indicator only.',
  },
  'bar-race.capShape': {
    description: 'Shape of the cap indicator: circle, square, roundedSquare, diamond. Per-entity override available via ElementPanel → Cap & image.',
    type:    'string',
    range:   "'circle' | 'square' | 'roundedSquare' | 'diamond'",
    default: 'circle',
    unit:    'enum',
    affects: ['cap visual shape', 'image clip boundary'],
    bounds:  ['The image inside the cap is clipped to the same shape.'],
    synergizes: ['bar-race.showCap', 'bar-race.capSizeMultiplier'],
    tensions:   [],
    agentNote: "Use 'circle' for country flags. 'square' for brand logos that look better uncropped. 'diamond' for award/sports aesthetics. Per-entity shape set via ElementPanel.",
  },
  'bar-race.capBorderWidth': {
    description: 'Width of the ring border drawn around the cap shape in canvas px. 0 = no border.',
    type:    'number',
    range:   '0–10',
    default: 2,
    unit:    'canvas px',
    affects: ['cap border ring'],
    bounds:  [],
    synergizes: ['bar-race.showCap'],
    tensions:   [],
    agentNote: 'Set to 0 for images with their own hard edges (e.g. logos with white backgrounds). Keep at 2 for clean separation of cap from bar and background.',
  },
  'bar-race.showScaleAxis': {
    description: 'Show vertical grid lines and numeric scale labels above the bars. Labels move as maxValue lerps between frames — the scale is always accurate to the current interpolated state.',
    type:    'boolean',
    range:   'true | false',
    default: true,
    unit:    'boolean',
    affects: ['scale context for viewers'],
    bounds:  [],
    synergizes: [],
    tensions:   [],
    agentNote: 'Keep true — the scale axis is the only way viewers can gauge absolute magnitudes from bar length. Without it, viewers only see relative rankings.',
  },
  'bar-race.entityNameFontFamily': {
    description: 'Font family for entity names in the left column. Default: Arial.',
    type:    'string',
    range:   'any web-safe font name',
    default: 'Arial',
    unit:    'font name',
    affects: ['entity name typography'],
    bounds:  [],
    synergizes: ['bar-race.entityNameFontSize', 'bar-race.entityNameFontWeight'],
    tensions:   [],
    agentNote: "Arial is universally available and clean. 'Georgia' for editorial/news feel. Always verify the font is web-safe before setting.",
  },
  'bar-race.valueLabelFontFamily': {
    description: 'Font family for value counters right of each bar. Default: Arial.',
    type:    'string',
    range:   'any web-safe font name',
    default: 'Arial',
    unit:    'font name',
    affects: ['value counter typography'],
    bounds:  [],
    synergizes: ['bar-race.valueLabelFontSize', 'bar-race.valueLabelFontWeight'],
    tensions:   [],
    agentNote: 'Keep consistent with entityNameFontFamily for typographic coherence.',
  },
  'bar-race.rankFontFamily': {
    description: 'Font family for rank numbers in the left margin. Default: Arial.',
    type:    'string',
    range:   'any web-safe font name',
    default: 'Arial',
    unit:    'font name',
    affects: ['rank number typography'],
    bounds:  [],
    synergizes: ['bar-race.rankFontSize', 'bar-race.rankFontWeight'],
    tensions:   [],
    agentNote: 'Rank numbers are secondary UI elements — using a lighter weight font (rankFontWeight 300–400) in the same family as entityNameFontFamily reads cleanest.',
  },
};